import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { branchAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

const fmtNum = n => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const Branches = () => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const pg = { color: darkMode ? '#E2E8F0' : '#1E293B' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const card = { background: darkMode ? '#1E293B' : '#FFFFFF', borderRadius: 12, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, padding: 20 };
  const inp = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#E2E8F0' : '#1E293B', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const btnPrimary = { padding: '8px 18px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' };
  const lbl = { ...muted, fontSize: 11, fontWeight: 600, marginBottom: 4, display: 'block' };

  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchSummary, setBranchSummary] = useState(null);

  const [form, setForm] = useState({ branch_name: '', branch_code: '', address: '', phone: '', email: '', gstin: '', state: '', is_main: false });

  const loadData = useCallback(async () => {
    setLoading(true);
    try { const r = await branchAPI.list(); setBranches(Array.isArray(r.data) ? r.data : []); } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const createBranch = async (e) => {
    e.preventDefault();
    try { await branchAPI.create(form); setShowForm(false); setForm({ branch_name: '', branch_code: '', address: '', phone: '', email: '', gstin: '', state: '', is_main: false }); loadData(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const viewSummary = async (branch) => {
    setSelectedBranch(branch);
    try { const r = await branchAPI.summary(branch.id); setBranchSummary(r.data); } catch (e) { console.error(e); }
  };

  const deleteBranch = async (id) => {
    if (!window.confirm('Delete this branch?')) return;
    try { await branchAPI.delete(id); loadData(); setSelectedBranch(null); } catch (e) { alert('Failed'); }
  };

  return (
    <Layout>
      <div className="page-enter" style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h1 style={{ ...pg, fontSize: 22, fontWeight: 800 }}>🏢 Branches</h1>
          <button onClick={() => setShowForm(!showForm)} style={btnPrimary}>{showForm ? 'Cancel' : '+ New Branch'}</button>
        </div>

        {showForm && (
          <form onSubmit={createBranch} style={{ ...card, marginBottom: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
              <div><label style={lbl}>Branch Name *</label><input value={form.branch_name} onChange={e => setForm(f => ({ ...f, branch_name: e.target.value }))} style={inp} required /></div>
              <div><label style={lbl}>Code</label><input value={form.branch_code} onChange={e => setForm(f => ({ ...f, branch_code: e.target.value }))} style={inp} placeholder="e.g. BR001" /></div>
              <div><label style={lbl}>Phone</label><input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>Address</label><input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>GSTIN</label><input value={form.gstin} onChange={e => setForm(f => ({ ...f, gstin: e.target.value }))} style={inp} /></div>
              <div><label style={lbl}>State</label><input value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} style={inp} /></div>
            </div>
            <button type="submit" style={btnPrimary}>Create Branch</button>
          </form>
        )}

        {loading && <div style={{ ...muted, textAlign: 'center', padding: 40 }}>Loading...</div>}

        {!loading && branches.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', ...muted, padding: 40 }}>No branches yet. Create your first branch to enable multi-location management.</div>
        ) : (
          <div className="stagger-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {branches.map(b => (
              <div key={b.id} style={{ ...card, cursor: 'pointer', borderLeft: `4px solid ${b.is_main ? acc : (darkMode ? '#475569' : '#CBD5E1')}`, transition: 'box-shadow 0.2s' }}
                onClick={() => viewSummary(b)}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <div style={{ ...pg, fontSize: 16, fontWeight: 700 }}>{b.branch_name}</div>
                    <div style={muted}>{b.branch_code || '—'} · {b.address || 'No address'}</div>
                    {b.state && <div style={{ ...muted, fontSize: 11 }}>State: {b.state}</div>}
                  </div>
                  {b.is_main && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, background: '#DBEAFE', color: '#1E40AF', fontWeight: 700 }}>MAIN</span>}
                </div>
                {b.gstin && <div style={{ ...muted, fontSize: 11, marginTop: 4 }}>GSTIN: {b.gstin}</div>}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={(e) => { e.stopPropagation(); deleteBranch(b.id); }} style={{ padding: '4px 10px', border: '1px solid #FCA5A5', borderRadius: 6, background: 'transparent', color: '#EF4444', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Branch Summary */}
        {selectedBranch && branchSummary && (
          <div style={{ ...card, marginTop: 24 }}>
            <h3 style={{ ...pg, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📊 {selectedBranch.branch_name} — Summary</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              <div style={{ padding: 16, borderRadius: 8, background: darkMode ? '#0F172A' : '#F8FAFC', textAlign: 'center' }}>
                <div style={muted}>Total Sales</div>
                <div style={{ ...pg, fontSize: 24, fontWeight: 800 }}>₹{fmtNum(branchSummary.total_sales)}</div>
              </div>
              <div style={{ padding: 16, borderRadius: 8, background: darkMode ? '#0F172A' : '#F8FAFC', textAlign: 'center' }}>
                <div style={muted}>Invoice Count</div>
                <div style={{ ...pg, fontSize: 24, fontWeight: 800 }}>{branchSummary.invoice_count || 0}</div>
              </div>
              <div style={{ padding: 16, borderRadius: 8, background: darkMode ? '#0F172A' : '#F8FAFC', textAlign: 'center' }}>
                <div style={muted}>Pending Amount</div>
                <div style={{ ...pg, fontSize: 24, fontWeight: 800 }}>₹{fmtNum(branchSummary.pending_amount)}</div>
              </div>
              <div style={{ padding: 16, borderRadius: 8, background: darkMode ? '#0F172A' : '#F8FAFC', textAlign: 'center' }}>
                <div style={muted}>Total Expenses</div>
                <div style={{ ...pg, fontSize: 24, fontWeight: 800 }}>₹{fmtNum(branchSummary.total_expenses)}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Branches;
